import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, Copy, Check, Mail, Package } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { ASSETS, PRESS, SUPPORT_EMAIL, DOMAIN } from "../data";
import StoreBadges, { StoreQRCodes } from "../components/StoreBadges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dl = (url, filename) => `${API}/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`;

const SCREENS = [
  { src: ASSETS.discover, label: "Discover" },
  { src: ASSETS.heroPhone, label: "Live map" },
  { src: ASSETS.goLive, label: "Go live" },
  { src: ASSETS.watchLive, label: "Watch live" },
  { src: ASSETS.dropBounty, label: "Drop a bounty" },
  { src: ASSETS.inboxTablet, label: "Inbox" },
  { src: ASSETS.mapcaster, label: "Creator profile" },
  { src: ASSETS.mapTablet, label: "Map on iPad" },
];

const CopyButton = ({ text, label, testid }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setDone(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <button onClick={copy} data-testid={testid} className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white text-sm font-semibold px-4 h-10 hover:bg-white/10 transition-colors">
      {done ? <Check className="w-4 h-4 text-mc-pink" /> : <Copy className="w-4 h-4" />} {label}
    </button>
  );
};

export default function Press() {
  usePageMeta({
    title: "Press Kit — MapCast",
    description: "Logo, app screenshots, brand colors, and boilerplate — everything you need to write about MapCast.",
    ogTitle: "MapCast Press Kit",
    ogDescription: "Logo, app screenshots, brand colors, and boilerplate — everything you need to write about MapCast.",
    ogImage: "https://mapcast.cc/assets/og-press.jpg",
    ogUrl: "https://mapcast.cc/press",
  });
  return (
    <div className="relative bg-mc-bg min-h-screen pt-36 pb-32 px-6 lg:px-10 spotlight">
      <div className="mx-auto max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-5">For press & partners</p>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">Press Kit</h1>
          <p className="mt-6 text-lg text-mc-muted font-light max-w-2xl leading-relaxed">
            Everything you need to write about MapCast — logo, app screenshots, brand colors, and
            boilerplate. For interviews or assets, reach us at {SUPPORT_EMAIL}.
          </p>
          <a
            href={`${API}/press-kit`}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-mc-bg font-semibold px-7 h-13 py-3.5 hover:scale-105 transition-transform duration-300"
            data-testid="press-download-all"
          >
            <Package className="w-5 h-5" /> Download all assets (.zip)
          </a>
        </motion.div>

        {/* Get the app */}
        <section className="mt-16" data-testid="press-download-section">
          <h2 className="font-display font-bold text-2xl text-white mb-6">Get the app</h2>
          <div className="glass rounded-3xl p-8 md:p-10 flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
            <div className="flex-1 text-center lg:text-left">
              <p className="text-mc-muted font-light leading-relaxed max-w-md">
                MapCast is live on the App Store and Google Play. Link directly to the store
                listings, or use the QR codes in articles, slides, and event material.
              </p>
              <StoreBadges className="mt-6 justify-center lg:justify-start" />
            </div>
            <StoreQRCodes className="lg:border-l lg:border-white/10 lg:pl-14" />
          </div>
        </section>

        {/* Logo */}
        <section className="mt-16" data-testid="press-logo-section">
          <h2 className="font-display font-bold text-2xl text-white mb-6">Logo</h2>
          <div className="glass rounded-3xl p-8 flex flex-col sm:flex-row items-center gap-8">
            <img src={ASSETS.icon} alt="MapCast icon" className="w-28 h-28 rounded-3xl ring-1 ring-white/15" />
            <div className="flex-1 text-center sm:text-left">
              <p className="text-mc-muted font-light">The MapCast app icon. Please keep clear space around the mark and don't recolor or distort it.</p>
              <a href={dl(ASSETS.icon, "mapcast-icon.jpg")} className="inline-flex items-center gap-2 mt-5 rounded-full bg-white text-mc-bg font-semibold px-6 h-12 hover:scale-105 transition-transform" data-testid="press-download-logo">
                <Download className="w-4 h-4" /> Download logo
              </a>
            </div>
          </div>
        </section>

        {/* Screenshots */}
        <section className="mt-16" data-testid="press-screens-section">
          <h2 className="font-display font-bold text-2xl text-white mb-6">App screenshots</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {SCREENS.map((s, i) => (
              <a key={i} href={dl(s.src, `mapcast-${s.label.toLowerCase().replace(/\s+/g, "-")}.jpg`)} className="group block" data-testid={`press-screen-${i}`}>
                <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[9/16] bg-mc-surface">
                  <img src={s.src} alt={s.label} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-mc-bg/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="inline-flex items-center gap-2 text-white text-sm font-semibold"><Download className="w-4 h-4" /> Download</span>
                  </div>
                </div>
                <p className="mt-2 text-sm text-mc-muted text-center">{s.label}</p>
              </a>
            ))}
          </div>
        </section>

        {/* Boilerplate */}
        <section className="mt-16" data-testid="press-boilerplate-section">
          <h2 className="font-display font-bold text-2xl text-white mb-6">Boilerplate</h2>
          <div className="space-y-6">
            <div className="glass rounded-3xl p-7">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-mc-muted">Short</span>
                <CopyButton text={PRESS.boilerplateShort} label="Copy" testid="press-copy-short" />
              </div>
              <p className="text-white/90 font-light leading-relaxed">{PRESS.boilerplateShort}</p>
            </div>
            <div className="glass rounded-3xl p-7">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-mc-muted">Long</span>
                <CopyButton text={PRESS.boilerplateLong} label="Copy" testid="press-copy-long" />
              </div>
              <p className="text-white/90 font-light leading-relaxed">{PRESS.boilerplateLong}</p>
            </div>
          </div>
        </section>

        {/* Colors + facts */}
        <section className="mt-16 grid md:grid-cols-2 gap-10">
          <div data-testid="press-colors-section">
            <h2 className="font-display font-bold text-2xl text-white mb-6">Brand colors</h2>
            <div className="grid grid-cols-2 gap-4">
              {PRESS.colors.map((c) => (
                <div key={c.hex} className="glass rounded-2xl p-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl ring-1 ring-white/15" style={{ background: c.hex }} />
                  <div>
                    <p className="text-white font-semibold text-sm">{c.name}</p>
                    <p className="text-mc-muted font-mono text-xs">{c.hex}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div data-testid="press-facts-section">
            <h2 className="font-display font-bold text-2xl text-white mb-6">Fast facts</h2>
            <div className="glass rounded-3xl divide-y divide-white/10">
              {PRESS.facts.map((f) => (
                <div key={f.k} className="flex items-center justify-between px-6 py-4">
                  <span className="font-mono text-xs uppercase tracking-[0.15em] text-mc-muted">{f.k}</span>
                  <span className="text-white font-medium text-right">{f.v}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 rounded-full bg-white text-mc-bg font-semibold px-5 h-11 hover:scale-105 transition-transform" data-testid="press-contact-btn">
                <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
              </a>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white/70 font-semibold px-5 h-11 cursor-default select-none" data-testid="press-live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-mc-live" /> Live now
              </span>
            </div>
          </div>
        </section>

        <p className="mt-16 text-sm text-mc-muted font-mono">{DOMAIN} · Press assets are provided for editorial use.</p>
      </div>
    </div>
  );
}
